import type {Iso8601DateTime, Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {iso8601DateTimeZodValidator, uuidZodValidator} from "@vesta/global-common-typescript/validators/zodValidators";
import type {UserDefinedObjectTemplate} from "@vesta/vos-types/modules/workspaceLevel/userDefinedObjects/domain/UserDefinedObjectTemplate";
import {UserDefinedObjectTemplateStatus} from "@vesta/vos-types/modules/workspaceLevel/userDefinedObjects/domain/UserDefinedObjectTemplate";
import type {WorkspaceCapability} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/workspaceCapabilities/domain/WorkspaceCapability";
import type {WorkspaceCapabilityType} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/workspaceCapabilities/domain/WorkspaceCapabilityType";
import {z as zod} from "zod";
import {
    type RetailLocationTemplateConfiguration,
    retailLocationTemplateConfigurationValidator,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/retailLocationsWorkspaceCapability/domain/RetailLocationTemplateConfiguration";

export enum RetailLocationTemplateStatus {
    active = "e0b46e99-5db5-4ec5-8507-217f7db5ef8d",
    archived = "f9af56ff-c91c-4ee6-9d3d-84445b06fd74",
}

export type RetailLocationTemplate = {
    configuration: RetailLocationTemplateConfiguration;
    createdAt: Iso8601DateTime;
    createdBy: Uuid;
    description: string | null;
    displayName: string;
    id: Uuid;
    organizationalUnitTemplateId: Uuid;
    organizationalUnitColumnId: Uuid;
    statusColumnId: Uuid;
    udoTemplateId: Uuid;
    status: RetailLocationTemplateStatus;
    updatedAt: Iso8601DateTime;
    updatedBy: Uuid;
};

export const retailLocationTemplateValidator: zod.ZodType<RetailLocationTemplate> = zod.strictObject({
    configuration: retailLocationTemplateConfigurationValidator,
    createdAt: iso8601DateTimeZodValidator,
    createdBy: uuidZodValidator,
    description: zod.string().nullable(),
    displayName: zod.string(),
    id: uuidZodValidator,
    organizationalUnitTemplateId: uuidZodValidator,
    organizationalUnitColumnId: uuidZodValidator,
    statusColumnId: uuidZodValidator,
    udoTemplateId: uuidZodValidator,
    status: zod.enum(RetailLocationTemplateStatus),
    updatedAt: iso8601DateTimeZodValidator,
    updatedBy: uuidZodValidator,
});

/** Compose internal domain views from independently versioned capability settings and UDO metadata. */
export function getRetailLocationTemplatesFromWorkspaceCapability({allUdoTemplates, workspaceCapability}: {
    allUdoTemplates: ReadonlyArray<UserDefinedObjectTemplate>;
    workspaceCapability: WorkspaceCapability<WorkspaceCapabilityType.retailLocations>;
}): Array<RetailLocationTemplate> {
    const entries = workspaceCapability.configuration.retailLocationTemplates;
    const bindings = workspaceCapability.udoBindings.retailLocationUdoTemplates;
    const templates = new Map(allUdoTemplates.filter(template => template.workspaceId === workspaceCapability.workspaceId).map(template => [template.id, template]));
    for (const key of Object.keys(bindings)) {
        if (entries[uuidZodValidator.parse(key)] === undefined) {
            throw new Error(`Retail Location template ${key} has a UDO binding without configuration`);
        }
    }
    return Object.entries(entries).map(([key, entry]) => {
        const id = uuidZodValidator.parse(key);
        const binding = bindings[id];
        if (binding === undefined) {
            throw new Error(`Retail Location template ${id} has no UDO binding`);
        }
        const template = templates.get(binding.udoTemplateId);
        if (template === undefined) {
            throw new Error(`UDO metadata ${binding.udoTemplateId} for Retail Location template ${id} is missing from this workspace`);
        }
        return {configuration: entry.configuration, createdAt: template.createdAt, createdBy: template.createdBy,
            description: template.description, displayName: template.displayName, id: id,
            organizationalUnitColumnId: entry.organizationalUnitColumnId, statusColumnId: entry.statusColumnId, organizationalUnitTemplateId: entry.organizationalUnitTemplateId,
            status: template.status === UserDefinedObjectTemplateStatus.active ? RetailLocationTemplateStatus.active : RetailLocationTemplateStatus.archived,
            udoTemplateId: template.id, updatedAt: template.updatedAt, updatedBy: template.updatedBy};
    });
}

/** Return business columns only; the internal OU link is exposed through organizationalUnitId. */
export function getRetailLocationAdditionalColumns({allUdoTemplates, template, workspaceId}: {
    allUdoTemplates: ReadonlyArray<UserDefinedObjectTemplate>;
    template: RetailLocationTemplate;
    workspaceId: Uuid;
}) {
    const metadata = allUdoTemplates.find(candidate => candidate.id === template.udoTemplateId && candidate.workspaceId === workspaceId);
    if (metadata === undefined) {
        throw new Error(`UDO metadata ${template.udoTemplateId} for Retail Location template ${template.id} is missing from this workspace`);
    }
    return metadata.additionalColumns.filter(column => column.id !== template.organizationalUnitColumnId && column.id !== template.statusColumnId);
}
